import React, { createElement, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { patchLink } from 'redux/links/actions';

import * as Icons from 'tabler-icons-react';

import CommonSearchInput from 'components/elements/inputs/CommonSearchInput';

import { Button, Modal } from 'semantic-ui-react';
import Parser from 'html-react-parser';
import SettingsSlider from 'components/dashboard/links-content/LinkIcons/SettingsSlider';
import LinkIconPreview from 'components/dashboard/links-content/LinkIcons/LinkIconPreview';

import IconColorPalete from 'components/dashboard/links-content/LinkIcons/IconColorPalete';

import './LinkIcons.css';

const LinkIcons = ({
  open,
  onClose,
  linkId,
  patchLink,
  itemThumbnailIcon,
}) => {
  const { t } = useTranslation();
  const iconRef = useRef();
  const [icons, setIcons] = useState(Object.entries(Icons));
  const [filter, setFilter] = useState('');
  const [activeIcon, setActiveIcon] = useState(null);
  const [thumbnailIcon, setThumbnailIcon] = useState(null);
  const [iconSize, setIconSize] = useState(32);
  const [iconStroke, setIconStroke] = useState(1.5);
  const [iconColor, setIconColor] = useState('#000000');

  // import all icons and set them to variable
  useEffect(() => {
    setIcons(
      Object.entries(Icons).filter(([name]) =>
        name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
      )
    );
  }, [filter]);

  // change | set icon for link
  useEffect(() => {
    if (thumbnailIcon) {
      const form = new FormData();
      form.append('thumbnail', '');
      form.append('thumbnail_icon', thumbnailIcon);
      patchLink(linkId, form);
      onClose();
    }
    return () => setThumbnailIcon(null);
  }, [thumbnailIcon]);

  // data destructuration to allow user edit icon
  useEffect(() => {
    const _itemThumbnailIcon = itemThumbnailIcon ? { element: Parser(itemThumbnailIcon)[0], name: Parser(itemThumbnailIcon)[1] } : null;
    if (_itemThumbnailIcon) {
      setIconSize(Number(_itemThumbnailIcon.element.props.width));
      setIconStroke(Number(_itemThumbnailIcon.element.props.strokeWidth));
      setIconColor(_itemThumbnailIcon.element.props.stroke);
    }
  }, [itemThumbnailIcon]);

  const handleActiveIcon = (event, obj) => {
    if (!activeIcon || (activeIcon && activeIcon.name !== obj.name)) {
      setActiveIcon(obj);
    } else {
      setActiveIcon(null);
      setThumbnailIcon(null);
    }
    return null;
  };

  const handleIconThumbnail = () => {
    setThumbnailIcon(iconRef.current.innerHTML);
  };

  const handleResetClick = () => {
    setIconSize(32);
    setIconStroke(1.5);
    setIconColor('#000000');
  };

  const parseItemThumbnailIcon = itemThumbnailIcon ? { element: Parser(itemThumbnailIcon)[0], name: Parser(itemThumbnailIcon)[1] } : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeIcon={!!onClose}
      className={`link-icons__component ${(activeIcon || itemThumbnailIcon) ? 'icon-selected' : ''}`}
      size={'small'}
    >
      <Modal.Header className="heading">{icons && icons.length} {t("Fully customizable free icons")}</Modal.Header>

      <Modal.Header className="link-icons-settings-header">
        <div className={`link-icons-settings-section ${(activeIcon || itemThumbnailIcon) ? 'visible' : 'hidden'}`}>
          <div className="link-icons-settings-section-wrapper">
            <div className="link-icons-settings-section-items">
              {/* Size */}
              <SettingsSlider
                label={t("Size")}
                min={12}
                max={44}
                defaultValue={iconSize}
                onChange={(value) => { setIconSize(value); }}
                value={iconSize}
                prefix="px"
              />
              {/* Stroke */}
              <SettingsSlider
                label={t("Stroke")}
                min={1}
                max={3}
                defaultValue={iconStroke}
                step={0.5}
                onChange={(value) => { setIconStroke(value); }}
                value={iconStroke}
                prefix="px"
              />
              {/* Color picker */}
              <SettingsSlider
                label={t("Color")}
                onChangeComplete={(value) => { setIconColor(value.hex); }}
                value={iconColor}
                type="color-picker"
              />
              {/* Reset */}
              <SettingsSlider
                onResetClick={() => { handleResetClick(); }}
                type="reset"
                value={null}
              />
            </div>
            {activeIcon &&
              <div
                className="icon-preview__component link-icons-settings-section-preview"
                ref={iconRef}
              >
                {createElement(activeIcon.icon, {
                  size: iconSize,
                  strokeWidth: iconStroke,
                  color: iconColor,
                })}
                <div className="icon-name">{activeIcon.name}</div>
              </div>
              }
              {!activeIcon && itemThumbnailIcon &&
                <div
                  className="icon-preview__component link-icons-settings-section-preview"
                  ref={iconRef}
                >
                  {createElement(parseItemThumbnailIcon.element.type, {
                    ...parseItemThumbnailIcon.element.props,
                    width: iconSize,
                    height: iconSize,
                    strokeWidth: iconStroke,
                    stroke: iconColor,
                  }, parseItemThumbnailIcon.element.props.children)}

                  <div className="icon-name">{parseItemThumbnailIcon.name.props.children}</div>
                </div>
              }
          </div>

          <IconColorPalete setIconColor={setIconColor} iconColor={iconColor} />

        </div>

        <CommonSearchInput
          className="link-icons-search-field"
          placeholder={t("Search icons")}
          options="shadow"
          onChange={(_e, { value }) => { setFilter(value); }}
        />
      </Modal.Header>

      <Modal.Content scrolling>
        <Modal.Description>
          <div className="link-icons-wrapper">
            {icons.map(iconName => {
              if (!icons) {
                return null;
              }

              return (
                <LinkIconPreview
                  key={`icon_preview_key_${iconName[0]}`}
                  className={(activeIcon && activeIcon.name === iconName[0] && 'active') || ''}
                  onClick={(e) =>
                    {
                      handleActiveIcon(e, {
                        name: iconName[0],
                        icon: iconName[1],
                        param: iconName,
                      });
                    }
                  }
                  activeIcon={{ name: iconName[0], icon: iconName[1] }}
                />
              );
            })}
          </div>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions className={(activeIcon || itemThumbnailIcon) ? 'visible' : 'hidden'}>
        <div className="link-icons-settings-actions">
          <Button onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button primary onClick={() => { handleIconThumbnail(); }}>
            {t("Apply")}
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};

LinkIcons.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  linkId: PropTypes.number.isRequired,
  patchLink: PropTypes.func,
  itemThumbnailIcon: PropTypes.object,
};
LinkIcons.defaultProps = {
  open: false,
  onClose: null,
  patchLink: null,
  itemThumbnailIcon: null,
};

export default connect(
  null,
  {
    patchLink,
  }
)(LinkIcons);
